import { Dialog, DialogContent, DialogTitle } from '../ui-primitives/dialog'
import React from 'react'
import { useEventLogs } from '../editor/event-logs/event-log-store'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../ui-primitives/resizable'
import { Badge } from '../ui-primitives/badge'
import { mergeClasses } from '../../helpers/styling'
import { Label } from '../ui-primitives/label'
import MultipleSelector from '../ui-primitives/multiple-selector'
import { getNamespaces, GLOBAL_NAMESPACE, usePubsub } from '../../redux/pubsub'
import { assertNever } from '../../utils/assert-never'

export function EventLogsPopup(props: {
  data?: undefined
  open: boolean
  onClose: () => void
}) {
  const [selectedId, setSelectedId] = React.useState<string>()
  const eventLogs = useEventLogs()
  const [includedNamespaces, setIncludedNamespaces] = React.useState<{value: string, label: string}[]>([])

  const [variables] = usePubsub()
  const namespaces = getNamespaces(variables)

  const filteredLogs = React.useMemo(() => eventLogs.filter(event => {
    switch (event.type) {
      case 'sparql-request-event':
      case 'construct-request-event':
      case 'app-variable-update-event': {
        if (event.namespace === GLOBAL_NAMESPACE) return true
        return includedNamespaces.some(n => n.label === event.namespace)
      }
      case 'rule-execution-event':
      case 'template-resolution-event': return true
      default: assertNever(event)
    }
  }), [eventLogs, includedNamespaces])

  if (!eventLogs.length) return (
    <DialogWrapper isOpen={props.open} close={props.onClose}>
      <div className="text-center px-4 py-2 opacity-75">
        No wistor-events have happened, since activating edit-mode.
      </div>
    </DialogWrapper>
  )

  const selected = eventLogs.find(log => log.id === selectedId)
  if (selectedId && !selected) {
    throw new Error('selected event-log-item not found')
  }

  return (
    <DialogWrapper isOpen={props.open} close={props.onClose}>
      <ResizablePanelGroup orientation="horizontal">

        <ResizablePanel className="overflow-auto h-full flex flex-col px-4 py-2" style={{
          height: "calc(100vh - 200px)"
        }}>
          <Label className="flex flex-col gap-1.5">
            <span>namespaces to include</span>
            <MultipleSelector
              value={includedNamespaces}
              onChange={setIncludedNamespaces}
              options={namespaces.map(n => ({label: n, value: n}))}
            />
          </Label>
          {filteredLogs.map(item => {
            switch (item.type) {

              case 'app-variable-update-event': return (
                <button
                  key={item.id}
                  className={mergeClasses(
                    "bg-yellow-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-300 ease-in-out hover:scale-105",
                    { "bg-opacity-30": item.id !== selectedId, "scale-105": item.id === selectedId }
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <Badge className="bg-yellow-600">
                    {item.namespace !== GLOBAL_NAMESPACE && `${item.namespace}: `}
                    VAR
                  </Badge>
                  <div className="grow overflow-hidden whitespace-nowrap">
                    <div className="overflow-hidden text-ellipsis">
                      {item.key} := {item.valueNew}
                    </div>
                    <div className="text-[0.6rem] leading-none text-pink-500">
                      {item.ctx}
                    </div>
                  </div>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>

                </button>
              )

              case 'sparql-request-event':
              case 'construct-request-event': return (
                <button
                  key={item.id}
                  className={mergeClasses(
                    "bg-green-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-100 ease-out hover:scale-105",
                    { "bg-opacity-30": item.id !== selectedId, "scale-105": item.id === selectedId }
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <Badge className="bg-green-600">
                    {item.namespace !== GLOBAL_NAMESPACE && `${item.namespace}: `}
                    {item.type === 'construct-request-event' ? 'CONSTRUCT' : 'SPARQL'}
                  </Badge>
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.source}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )

              case 'rule-execution-event': return (
                <button
                  key={item.id}
                  className={mergeClasses(
                    "bg-purple-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-300 ease-in-out hover:scale-105",
                    { "bg-opacity-30": item.id !== selectedId, "scale-105": item.id === selectedId }
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <Badge className="bg-purple-600">RULE</Badge>
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.tag}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )


              case 'template-resolution-event': return (
                <button
                  key={item.id}
                  className={mergeClasses(
                    "bg-slate-100 flex flex-row text-left space-x-1 items-center font-mono text-sm border-y transition duration-300 ease-in-out hover:scale-105",
                    { "bg-opacity-30": item.id !== selectedId, "scale-105": item.id === selectedId }
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  <Badge className="bg-slate-600">TEMPLATE RESOLUTION</Badge>
                  <span className="grow overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.template}
                  </span>
                  <span className="font-mono opacity-75 text-xs">[{item.timestamp}]</span>
                </button>
              )


              default: {
                assertNever(item)
              }
            }
        })}
        </ResizablePanel>

        <ResizableHandle withHandle className="mx-1" />

        <ResizablePanel className="overflow-auto h-full flex flex-col gap-3 px-4 py-2 text-sm" style={{
          height: "calc(100vh - 200px)"
        }}>{(() => {
          switch (selected?.type) {

            case 'app-variable-update-event': return (<>
              {selected.ctx && (
                <span className="opacity-75 text-pink-500">{selected.ctx}</span>
              )}
              <div className="flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">app-variable update</code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div>
                <span className="flex opacity-75">variable identifier</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.key}</pre>
              </div>
              <div>
                <span className="flex opacity-75">old value</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.valueOld}</pre>
              </div>
              <div>
                <span className="flex opacity-75">new value</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.valueNew}</pre>
              </div>
            </>)

            case 'sparql-request-event':
            case 'construct-request-event': return (<>
              <div className="flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">
                    {selected.type === 'construct-request-event' ? 'construct request' : 'sparql request'}
                  </code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div>
                <span className="flex opacity-75">request</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.request}</pre>
              </div>
              <div>
                <span className="flex opacity-75">response</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono whitespace-pre-wrap">{selected.response}</pre>
              </div>
            </>)

            case 'rule-execution-event': return (<>
              <div className="flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">rule execution</code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div>
                <span className="flex opacity-75">tag</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.tag}</pre>
              </div>
              <div>
                <span className="flex opacity-75">parameters</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.params}</pre>
              </div>
            </>)

            case 'template-resolution-event': return (<>
              <div className="flex flex-row justify-between">
                <div>
                  <span className="opacity-75 mr-2">type</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">template resolution</code>
                </div>
                <div>
                  <span className="opacity-75 mr-2">timestamp</span>
                  <code className="select-all text-black bg-slate-100 p-1 rounded-sm font-mono">{selected.timestamp}</code>
                </div>
              </div>
              <div>
                <span className="flex opacity-75">template</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.template}</pre>
              </div>
              <div>
                <span className="flex opacity-75">resolutions</span>
                <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.partialResults}</pre>
              </div>
              {selected.endResult && (
                <div>
                  <span className="flex opacity-75">result</span>
                  <pre className="select-all bg-slate-100 p-1 rounded-sm font-mono">{selected.endResult}</pre>
                </div>
              )}
            </>)

            case undefined: return null

            default: {
              assertNever(selected)
            }
          }
        })()}</ResizablePanel>
      </ResizablePanelGroup>
    </DialogWrapper>
  )
}

function DialogWrapper(props: {
  children: React.ReactNode,
  isOpen: boolean,
  close: () => void,
}): React.ReactNode {
  return (
    <Dialog open={props.isOpen} onOpenChange={open => {
      if (!open) props.close()
    }}>
      <DialogContent
        className="gap-0 p-0 max-w-[1536px] w-[calc(100%-64px)] overflow-hidden rounded-md"
        closeButtonClassName="right-2 top-1.5 text-wistor-foreground"
        style={{ fontFamily: "'Exo 2 Variable', sans-serif" }}
        aria-describedby={undefined}
      >
        <DialogTitle className="text-base font-normal text-center sm:text-left bg-wistor text-wistor-foreground px-2 py-1">
          Event Logs
        </DialogTitle>
        {props.children}
      </DialogContent>
    </Dialog>
  )
}
